import { cn } from '../lib/cn.js';
import { t } from '../i18n/index.js';

/**
 * Skeleton — a placeholder while data loads.
 *
 * ── ONE RULE, AND IT MATTERS HERE MORE THAN MOST PLACES ───────────────────
 *
 * A skeleton must never take the shape of a NUMBER. A grey bar where "₹42,54,999"
 * will appear is fine. A grey bar that is 8 characters wide, right-aligned, in
 * the amount column, reads for a moment as though a figure is there — and in a
 * financial tool a figure that is not real, even for 200ms, is the wrong thing
 * to show. `TableSkeleton` therefore fills whole cells rather than mimicking
 * amounts.
 *
 * Skeletons are hidden from assistive tech; the region announces its own busy
 * state. A screen reader user gets "loading" once, not forty times.
 */

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('block animate-pulse rounded-sm bg-sunken', className)}
    />
  );
}

/** Rows of placeholder cells matching a table's shape, so nothing reflows. */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div aria-busy="true" aria-live="polite" className="w-full">
      <span className="sr-only">{t('shell.loading')}</span>
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="flex items-center gap-4 border-b border-(--table-border) px-(--table-cell-padding-x) h-(--table-row-height) last:border-b-0"
        >
          {Array.from({ length: columns }).map((_, c) => (
            <Skeleton
              key={c}
              className={cn(
                'h-2.5',
                // Varying widths so the block does not read as a rendered grid,
                // deterministic so it does not shimmer differently each render.
                c === 0 ? 'w-16' : c === columns - 1 ? 'w-12 ml-auto' : 'flex-1 max-w-32',
              )}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
